import { Link } from "react-router-dom";
import { Reveal } from "@/lib/motion";
import { LayeredDocuments } from "@/components/site/Decorations";
import { ArrowUpRight } from "lucide-react";

const OFFERS = [
  "2Tap — Office in your pocket",
  "Contracts Administration Services",
  "Sales & Marketing Services",
  "Business Systems Review",
  "AI Workflow Setup",
];

export default function OffersPreview() {
  return (
    <section
      data-testid="offers-preview-section"
      className="py-20 md:py-28 bg-bg-muted border-y border-ink/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-10 md:mb-14">
          <Reveal>
            <div className="overline">Specialist offers</div>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] max-w-2xl">
              Where the pillars
              <em className="font-light text-ink-soft"> become product</em>.
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="max-w-md text-ink/75 leading-relaxed">
              Productised services and systems engagements you can step into
              when you're ready to modernise a specific part of your business.
            </p>
          </Reveal>
        </div>

        {/* Featured 2Tap card — kept on home as the brand proof point */}
        <Reveal>
          <a
            href="https://2tap.app/"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="featured-2tap-card"
            className="relative block bg-bg-ink text-bg-base p-8 md:p-12 group overflow-hidden border border-ink"
          >
            <div className="absolute inset-0 paper-grain opacity-15 mix-blend-screen pointer-events-none" />
            <LayeredDocuments className="absolute -right-4 -bottom-6 w-40 md:w-52 text-bg-base/55 pointer-events-none hidden sm:block" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                <div className="overline text-brand-brassSoft font-semibold">
                  Featured proof — reimagining work
                </div>
                <h3 className="mt-4 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-bg-base">
                  2Tap.{" "}
                  <em className="font-light text-bg-base/95">Office in your pocket.</em>
                </h3>
                <p className="mt-5 max-w-xl text-bg-base/95 leading-relaxed text-[15.5px] md:text-base">
                  An example of ETradingGroup's systems thinking in practice —
                  voice capture, structured workflows and AI-supported task
                  handling, built for site-based and project-based businesses.
                </p>
              </div>
              <div className="lg:col-span-4 lg:justify-self-end">
                <span className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 text-brand-brassSoft text-[13px] tracking-[0.06em] font-semibold px-4 py-2.5 transition-colors border border-brand-brassSoft/40">
                  Visit 2tap.app
                  <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </span>
              </div>
            </div>
          </a>
        </Reveal>

        {/* Compact inline offer pills */}
        <Reveal>
          <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-3 gap-y-3">
            <span className="overline text-ink-soft mr-1">Also available</span>
            {OFFERS.slice(1).map((o) => (
              <Link
                key={o}
                to="/services"
                data-testid={`offer-pill-${o}`}
                className="inline-flex items-center gap-2 border border-ink/25 hover:border-ink hover:bg-ink hover:text-white px-3.5 py-1.5 text-[12.5px] tracking-[0.02em] transition-colors"
              >
                {o}
              </Link>
            ))}
          </div>
        </Reveal>

        <Reveal delay={1}>
          <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-ink-soft text-[15px] max-w-md">
              Browse the full breakdown — what each offer includes and who it suits.
            </p>
            <Link
              to="/services"
              data-testid="offers-preview-cta"
              className="group inline-flex items-center gap-3 bg-ink text-white hover:bg-brand-red px-6 py-3.5 text-[13px] tracking-[0.06em] font-medium transition-colors"
            >
              View all specialist offers
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
